// Возвращает короткий идентификатор браузера, например "YaBrowser/24" или "Safari/17".
//
// Generic-детект без списка распознаваемых браузеров: игнор-список содержит только
// движковые/совместимостные имена, а бренд извлекается сам. Поэтому новый (в том числе
// ещё не существующий) браузер определяется автоматически, без правки кода.

type UserAgentBrand = { brand?: string; version?: string }

// Движковые и совместимостные токены — это НЕ имя браузера. Всё, что встречается в
// User-Agent, но не входит сюда, считаем брендом браузера.
const ENGINE_TOKENS = new Set([
    'Mozilla',
    'AppleWebKit',
    'KHTML',
    'Gecko',
    'Version',
    'Safari',
    'Chrome',
    'Mobile',
    'like',
])

function majorVersion(version: string | undefined): string {
    return version ? version.split('.')[0] : ''
}

function withVersion(brand: string, version: string | undefined): string {
    const major = majorVersion(version)
    return major ? brand + '/' + major : brand
}

// grease-запись — служебный «браузер», который Chromium подмешивает в brands, чтобы
// сайты не хардкодили список. Вид: "Not.A/Brand", "Not_A Brand", "Not-A.Brand".
function isGreaseBrand(brand: string): boolean {
    return brand.includes('Not') && brand.includes('Brand')
}

// 1. User-Agent Client Hints (low-entropy, синхронно). Chromium-браузер сам называет
//    свой настоящий бренд — остаётся отфильтровать "Chromium" и grease-записи.
export function brandFromClientHints(brands: UserAgentBrand[] | undefined): string | null {
    if (!Array.isArray(brands)) return null
    const real = brands.find(
        (b): b is Required<UserAgentBrand> =>
            !!b?.brand && b.brand !== 'Chromium' && !isGreaseBrand(b.brand),
    )
    return real ? withVersion(real.brand, real.version) : null
}

// 2. Fallback по UA-строке (Firefox/Safari/старьё/нет userAgentData). Разбираем токены
//    вида «Имя/Версия» и выкидываем движковые — оставшийся токен и есть бренд.
export function brandFromUserAgent(ua: string): string | null {
    const tokens = Array.from(ua.matchAll(/([A-Za-z][A-Za-z0-9._-]*)\/([\d.]+)/g))
    const brand = tokens.find(([, name]) => !ENGINE_TOKENS.has(name))
    if (brand) return withVersion(brand[1], brand[2])

    // Распознавательного токена нет → это «чистый» Chrome или Safari: они называют
    // себя только движковыми токенами (Chrome/…, либо Version/… Safari/… без Chrome).
    const safari = ua.match(/Version\/([\d.]+).*Safari/)
    if (safari && !ua.includes('Chrome/')) return withVersion('Safari', safari[1])
    const chrome = ua.match(/Chrome\/([\d.]+)/)
    if (chrome) return withVersion('Chrome', chrome[1])
    return null
}

export function parseUserAgent(ua: string, brands: UserAgentBrand[] | undefined): string {
    return brandFromClientHints(brands) ?? brandFromUserAgent(ua) ?? ua.slice(0, 30)
}

// Осознанный whitelist: сырой бренд (без версии) → каноническое имя из нашего ENUM.
// Ключи в нижнем регистре — сопоставление регистронезависимое. В отличие от generic
// `browserShortName` («как есть»), это нормализованное имя для группировки в статистике;
// любой нераспознанный браузер попадает в 'Other', но при этом остаётся виден в `ua`.
const CANONICAL_BROWSERS: Record<string, string> = {
    'chrome': 'Chrome',
    'google chrome': 'Chrome',
    'crios': 'Chrome',
    'edg': 'Edge',
    'edga': 'Edge',
    'edgios': 'Edge',
    'microsoft edge': 'Edge',
    'yabrowser': 'Yandex',
    'yandex': 'Yandex',
    'opr': 'Opera',
    'opera': 'Opera',
    'firefox': 'Firefox',
    'fxios': 'Firefox',
    'safari': 'Safari',
    'samsungbrowser': 'Samsung',
    'chromium': 'Chromium',
}

// Каноническое имя браузера из нашего ENUM известных браузеров. Вход — «сырой» бренд
// БЕЗ версии (токен до «/»). Сопоставление регистронезависимое; неизвестный → 'Other'.
export function canonicalBrowser(name: string): string {
    return CANONICAL_BROWSERS[name.toLowerCase()] ?? 'Other'
}

function detectBrowserShortName(): string {
    if (typeof navigator === 'undefined') return 'unknown'
    const brands = (navigator as any).userAgentData?.brands as UserAgentBrand[] | undefined
    return parseUserAgent(navigator.userAgent, brands)
}

export const browserShortName = detectBrowserShortName()
// Каноническое имя из того же детекта: отбрасываем версию (токен до «/») и нормализуем.
export const browserCanonical = canonicalBrowser(browserShortName.split('/')[0])
